import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Support as SupportIcon,
  ShoppingCart as SalesIcon,
} from '@mui/icons-material';

export default function SalesSupport() {
  const [activeTab, setActiveTab] = useState(0);
  const [salesUsers, setSalesUsers] = useState([]);
  const [supportUsers, setSupportUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    department: "",
    active: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/staff?grouped=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSalesUsers(response.data.sales || []);
      setSupportUsers(response.data.support || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null, type = null) => {
    if (user) {
      setEditingUser({ ...user, type: user.type || type });
      setFormData({
        name: user.name,
        email: user.email,
        password: "",
        phone: user.phone || "",
        department: user.department || "",
        active: user.active,
      });
    } else {
      setEditingUser({ type: activeTab === 0 ? 'sales' : 'support' });
      setFormData({
        name: "",
        email: "",
        password: "",
        phone: "",
        department: "",
        active: true,
      });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingUser(null);
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        role: editingUser.type === 'sales' ? 'sales' : 'support'
      };
      
      if (editingUser?.id) {
        await axios.put(`${API}/admin/staff/${editingUser.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Usuário atualizado!");
      } else {
        await axios.post(`${API}/admin/staff`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Usuário criado!");
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      toast.error(error.response?.data?.message || "Erro ao salvar usuário");
    }
  };

  const handleDelete = async (userId, type) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuário?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/staff/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Usuário excluído!");
      
      if (type === 'sales') {
        setSalesUsers(salesUsers.filter(u => u.id !== userId));
      } else {
        setSupportUsers(supportUsers.filter(u => u.id !== userId));
      }
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error("Erro ao excluir usuário");
    }
  };

  const handleToggleActive = async (userId, active, type) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/staff/${userId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Usuário ${!active ? 'ativado' : 'desativado'}!`);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error("Erro ao atualizar status");
    }
  };

  const renderUserTable = (users, type) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Nome</strong></TableCell>
            <TableCell><strong>Email</strong></TableCell>
            <TableCell><strong>Telefone</strong></TableCell>
            <TableCell><strong>Departamento</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell align="center"><strong>Ações</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary" py={3}>
                  Nenhum usuário cadastrado
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="600">
                    {user.name}
                  </Typography>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.phone || '-'}</TableCell>
                <TableCell>{user.department || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={user.active ? 'Ativo' : 'Inativo'}
                    color={user.active ? 'success' : 'default'}
                    size="small"
                    onClick={() => handleToggleActive(user.id, user.active, type)}
                    sx={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(user, type)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(user.id, type)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Vendedores e Suporte
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie a equipe de vendas e suporte da plataforma
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo {activeTab === 0 ? 'Vendedor' : 'Suporte'}
        </Button>
      </Box>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<SalesIcon />}
            label={`Vendedores (${salesUsers.length})`}
            iconPosition="start"
          />
          <Tab
            icon={<SupportIcon />}
            label={`Suporte (${supportUsers.length})`}
            iconPosition="start"
          />
        </Tabs>

        {/* Tab Content */}
        <Box>
          {activeTab === 0 && renderUserTable(salesUsers, 'sales')}
          {activeTab === 1 && renderUserTable(supportUsers, 'support')}
        </Box>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingUser?.id ? 'Editar' : 'Novo'} {editingUser?.type === 'sales' ? 'Vendedor' : 'Suporte'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome Completo"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Senha"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingUser?.id}
                  helperText={editingUser?.id ? "Deixe em branco para manter a senha atual" : ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Departamento/Especialidade"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder={
                    editingUser?.type === 'sales' 
                      ? "Ex: Vendas Corporativas, Vendas PME" 
                      : "Ex: Suporte Técnico, Suporte Financeiro"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  name="active"
                  value={formData.active}
                  onChange={handleChange}
                >
                  <MenuItem value={true}>Ativo</MenuItem>
                  <MenuItem value={false}>Inativo</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingUser?.id ? 'Salvar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
